// src/services/managerService.js
import api from '@/core/api/api';

const managerService = {
  // Get manager portfolio (firm details and funds in a single call)
  getPortfolio: async () => {
    try {
      const response = await api.get('/fetch/manager_portfolio');
      return response.data;
    } catch (error) {
      console.error('Error fetching manager portfolio:', error);
      throw error;
    }
  },
  
  // Get manager funds  
  getFunds: async () => {
    try {
      const response = await api.get('/fetch/manager_funds');
      return response.data;
    } catch (error) {
      console.error('Error fetching manager funds:', error);
      throw error;
    }
  },
  
  // Create a new fund
  createFund: async (fundData) => {
    try {
      const response = await api.post('/create/manager_funds', fundData);
      return response.data;
    } catch (error) {
      console.error('Error creating fund:', error);
      throw error;
    }
  },
  
  // Get specific fund details
  getFundDetails: async (fundId) => {
    try {
      const response = await api.get('/fetch/manager_funds', { 
        params: { fundId }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching fund details:', error);
      throw error;
    }
  },
  
  // Update fund details
  updateFund: async (fundId, fundData) => {
    try {
      const response = await api.put('/update/manager_funds', {
        ...fundData,
        fundId
      });
      return response.data;
    } catch (error) {
      console.error('Error updating fund:', error);
      throw error;
    }
  },
  
  // Delete a fund
  deleteFund: async (fundId) => {
    try {
      const response = await api.delete('/delete/manager_funds', {
        params: { fundId }
      });
      return response.data;
    } catch (error) {
      console.error('Error deleting fund:', error);
      throw error;
    }
  },
  
// ----- CO-INVESTMENT METHODS -----

getCoInvestments: async (userId) => {
  try {
    console.log('Fetching co-investments from API...');
    const response = await api.get('/managers/opportunities', {
      params: { manager_id: userId }
    });
    
    console.log('API Response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching co-investments:', error);
    throw error;
  }
},

  // Get a specific co-investment by ID
  getCoInvestmentDetails: async (coInvestmentId) => {
    try {
      const response = await api.get('/managers/opportunities/fetch_details', {
        params: { 
          co_invest_id: coInvestmentId
        }
      });
      
      console.log('Raw API Response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching co-investment details:', error);
      throw error;
    }
  },
  
  // Create a new opportunity
  createOpportunity: async (opportunityData) => {
    // Real API call using new maintain_opportunities endpoint
    const response = await api.post('/managers/maintain_opportunities', {
      manager_id: opportunityData.co_invest_manager_id || opportunityData.manager_id,
      form_data: opportunityData
    });
    
    const result = response.data;
    
    // Note: Profile generation is triggered by OpportunityProfileModal (same as RegistrationProfileModal pattern)
    // The wizard opens the modal, then the modal initiates generation with UI feedback
    // Engagement tracking is handled automatically by the backend based on co_invest_visible flag
    
    return result;
  },
  
  // Update an existing opportunity
  updateOpportunity: async (opportunityId, opportunityData) => {
    // Real API call using new maintain_opportunities endpoint
    const response = await api.put('/managers/maintain_opportunities', {
      manager_id: opportunityData.co_invest_manager_id || opportunityData.manager_id,
      co_invest_id: opportunityId,
      form_data: opportunityData
    });
    
    // Note: Profile regeneration is triggered by OpportunityProfileModal (same as RegistrationProfileModal pattern)
    // The wizard opens the modal, then the modal initiates generation with UI feedback
    
    return response.data;
  },
  
  // Delete a co-investment
  deleteCoInvestment: async (coInvestmentId) => {
    // Real API call
    const response = await api.delete('/delete/manager_coinvestment', {
      params: { co_invest_id: coInvestmentId }
    });
    return response.data;
  },

  // Create co-investment opportunity using new multi-step approach
  createCoInvestmentOpportunity: async (opportunityData) => {
    try {
      const response = await api.post('/managers/create_coinvestment_opportunity', {
        form_data: opportunityData,
        manager_id: opportunityData.manager_id,
        firm_id: opportunityData.firm_id
      });
      return response.data;
    } catch (error) {
      console.error('Error creating co-investment opportunity:', error);
      throw error;
    }
  },

  // Generate opportunity marketing content using AI (fire-and-forget)
  generateOpportunityContent: async (requestData) => {
    try {
      console.log('Starting AI content generation (fire-and-forget)...');
      const response = await api.post('/managers/opportunities/generate_content', requestData);
      console.log('Content generation started:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error starting content generation:', error);
      throw error;
    }
  },

  // Get generated opportunity content after completion
  getGeneratedContent: async (generationId) => {
    try {
      console.log('Fetching generated content for:', generationId);
      const response = await api.get(`/managers/opportunities/get_generated_content/${generationId}`);
      console.log('Generated content response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching generated content:', error);
      throw error;
    }
  },

  // Check content generation status (uses existing document_status endpoint)
  checkGenerationStatus: async (generationId) => {
    try {
      const response = await api.get(`/manage_docs/document_status/${generationId}`);
      return response.data;
    } catch (error) {
      console.error('Error checking generation status:', error);
      throw error;
    }
  },

  // Get firm team members
  getFirmTeamMembers: async (firmId) => {
    try {
      console.log('Fetching team members for firm:', firmId);
      const response = await api.get('/entities/team', {
        params: { firm_id: firmId }
      });
      console.log('Team members response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching firm team members:', error);
      throw error;
    }
  },
  
  // Get co-investment participants
  getCoInvestmentParticipants: async (coInvestmentId) => {
    // Real API call
    const response = await api.get('/fetch/manager_coinvestment/participants', {
      params: { co_invest_id: coInvestmentId }
    });
    return response.data;
  },

  // Get all investors for the manager (complete list of investors)
  getInvestors: async ({ page = 1, pageSize = 10 } = {}) => {
    try {
      console.log('Fetching investors...');
      const response = await api.get('/fetch/investors', {
        params: { page, pageSize }
      });
      console.log('API Response:', response.data);

      // Return the data as is, without transformation
      return {
        investors: response.data,
        total: response.data.length // This should be updated to use actual total from backend
      };
    } catch (error) {
      console.error('Error fetching investors:', error);
      throw error;
    }
  },

  // Get a specific investor by ID
  getInvestor: async (investorId) => {
    try {
      console.log('Fetching investor details from API...');
      const response = await api.get('/fetch/investors', {
        params: { investor_id: investorId }
      });
      console.log('API Response:', response.data);

      const investor = response.data.investors?.[0];
      
      if (!investor) {
        throw new Error('Investor not found');
      }

      // Transform the comprehensive response to match frontend expectations
      const transformedInvestor = {
        // Core identification
        id: investor.investor_id || investor.id,
        name: investor.investor_name || investor.name || investor.displayName,
        email: investor.investor_email || investor.email,
        phone: investor.phone,
        
        // Status and type
        status: investor.investor_status || investor.status,
        type: investor.investor_type || investor.type,
        
        // Organization (for institutional investors)
        organization: investor.investor_organization || investor.organization,
        website: investor.website,
        foundedYear: investor.foundedYear,
        institutionType: investor.institutionType,
        aum: investor.aum,
        description: investor.description,
        decisionMakingProcess: investor.decisionMakingProcess,
        title: investor.title,
        
        // Address information
        address: investor.address,
        city: investor.city,
        state: investor.state,
        postalCode: investor.postalCode,
        country: investor.country,
        fullAddress: investor.fullAddress,
        
        // Investment profile
        investmentSize: investor.investmentSize,
        investmentSizeDisplay: investor.investmentSizeDisplay,
        preferredCommunication: investor.preferredCommunication,
        investmentExperience: investor.investmentExperience,
        experienceLevel: investor.experienceLevel,
        riskTolerance: investor.riskTolerance,
        investmentHorizon: investor.investmentHorizon,
        interests: investor.interests || [],
        
        // HNWI-specific fields (for individual investors)
        netWorthRange: investor.netWorthRange,
        annualIncomeRange: investor.annualIncomeRange,
        occupation: investor.occupation,
        referralSource: investor.referralSource,
        liquidityPreference: investor.liquidityPreference,
        
        // Profile flags
        hasInstitutionProfile: investor.has_institution_profile,
        hasInvestmentProfile: investor.has_investment_profile,
        hasFirmProfile: investor.has_firm_profile,
        hasFundProfile: investor.has_fund_profile,
        
        // Agreement and registration
        agreementAccepted: investor.agreement_accepted,
        agreementAcceptedAt: investor.agreement_accepted_at,
        agreementVersion: investor.agreement_version,
        registrationStatus: investor.registration_status,
        approvalStatus: investor.approval_status,
        
        // Timestamps
        investmentsCount: investor.investments_count || investor.investmentsCount || 0,
        lastActive: investor.last_active_date || investor.lastActive,
        createdAt: investor.created_at || investor.createdAt,
        
        // Investment history
        investments: investor.investments?.map(investment => ({
          id: investment.investment_id || investment.id,
          name: investment.investment_name || investment.name,
          type: investment.investment_type || investment.type,
          amount: investment.investment_amount || investment.amount,
          currency: investment.investment_currency || investment.currency,
          date: investment.investment_date || investment.date,
          status: investment.investment_status || investment.status
        })) || [],
        
        // Additional metadata
        dataSource: investor.data_source,
        referenceId: investor.investor_reference_id,
        statusDisplay: investor.statusDisplay,
        displayName: investor.displayName
      };

      console.log('Transformed investor data:', transformedInvestor);
      return transformedInvestor;
    } catch (error) {
      console.error('Error fetching investor details:', error);
      throw error;
    }
  },

  getInvestorDetails: async (investorId) => {
    try {
      console.log('Fetching investor details...');
      const response = await api.get('/fetch/investors/details', {
        params: { investor_id: investorId }
      });
      console.log('API Response:', response.data);

      const item = response.data;
      return {
        id: item.user_info.user_id,
        name: `${item.user_info.user_first_name} ${item.user_info.user_last_name}`,
        email: item.user_info.user_email,
        status: item.user_info.user_status,
        type: item.investor_info.investor_type,
        // Common fields
        country: item.additional_info.country,
        interests: item.investor_info.investment_interests,
        lastActive: item.user_info.updated_at,
        createdAt: item.user_info.created_at,
        // Institutional specific fields
        organization: item.additional_info.name,
        aum: item.additional_info.estimated_aum,
        investmentSize: item.additional_info.typical_investment_size,
        website: item.additional_info.website,
        address: item.additional_info.address,
        city: item.additional_info.city,
        state: item.additional_info.state,
        postalCode: item.additional_info.postal_code,
        foundedYear: item.additional_info.founded_year,
        preferredCommunication: item.additional_info.preferred_communication,
        description: item.additional_info.description,
        institutionType: item.additional_info.type,
        title: item.additional_info.title,
        decisionMakingProcess: item.additional_info.decision_making_process,
        // HNWI specific fields
        phone: item.investor_info.phone,
        investmentExperience: item.investor_info.investment_experience,
        riskTolerance: item.investor_info.risk_tolerance,
        investmentHorizon: item.investor_info.investment_horizon,
        netWorth: item.investor_info.net_worth,
        annualIncome: item.investor_info.annual_income
      };
    } catch (error) {
      console.error('Error fetching investor details:', error);
      throw error;
    }
  },

  // ===== MANAGER PREFERENCES =====
  
  /**
   * Get manager preferences
   * @param {string} managerId - Manager user ID
   */
  getPreferences: async (managerId) => {
    try {
      const response = await api.get('/managers/settings/preferences', {
        params: { manager_id: managerId }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching manager preferences:', error);
      throw error;
    }
  },

  /**
   * Update manager preferences
   * @param {Object} preferences - Preference object to update
   * @param {string} managerId - Manager user ID
   */
  updatePreferences: async (preferences, managerId) => {
    try {
      const response = await api.post('/managers/settings/preferences', {
        manager_id: managerId,
        manager_preference: preferences
      });
      return response.data;
    } catch (error) {
      console.error('Error updating manager preferences:', error);
      throw error;
    }
  },

  // ===== DOCUMENT EXTRACTION METHODS =====
  
  /**
   * Process firm document for extraction (upload + trigger processing)
   * @param {File} file - The document file to upload
   * @param {string} firmId - The firm ID
   * @param {string} uploadedBy - User email/ID
   */
  processEditFirmDocument: async (file, firmId, uploadedBy) => {
    try {
      const { getUploadUrl, putToPresignedUrl } = await import('@/services/documentService');
      const { presigned_url, document_id, s3_bucket, s3_key } = await getUploadUrl(
        'extract_firm_edit',
        {
          file_name: file.name,
          content_type: file.type || 'application/octet-stream',
          uploaded_by: uploadedBy,
          firm_id: firmId,
        }
      );
      await putToPresignedUrl(presigned_url, file, file.type || 'application/octet-stream');

      // Fire-and-forget extraction trigger (same as before)
      api.post('/managers/maintain_firm/extract/edit_firm', {
        document_id,
        s3_bucket,
        s3_key,
        firm_id: firmId,
        uploaded_by: uploadedBy,
      }).catch(err => {
        console.error('Firm extraction trigger failed:', err);
      });

      return { document_id };
    } catch (error) {
      console.error('Error processing firm document:', error);
      throw new Error(
        error.response?.data?.error ||
        error.message ||
        'Failed to process firm document. Please try again.'
      );
    }
  },

  /**
   * Process team document for extraction (upload + trigger processing)
   * @param {File} file - The document file to upload
   * @param {string} firmId - The firm ID
   * @param {string} uploadedBy - User email/ID
   */
  processEditTeamDocument: async (file, firmId, uploadedBy) => {
    try {
      const { getUploadUrl, putToPresignedUrl } = await import('@/services/documentService');
      const { presigned_url, document_id, s3_bucket, s3_key } = await getUploadUrl(
        'extract_team_edit',
        {
          file_name: file.name,
          content_type: file.type || 'application/octet-stream',
          uploaded_by: uploadedBy,
          firm_id: firmId,
        }
      );
      await putToPresignedUrl(presigned_url, file, file.type || 'application/octet-stream');

      // Fire-and-forget extraction trigger (same as before)
      api.post('/managers/maintain_firm/extract/edit_team', {
        document_id,
        s3_bucket,
        s3_key,
        firm_id: firmId,
        uploaded_by: uploadedBy,
      }).catch(err => {
        console.error('Team extraction trigger failed:', err);
      });

      return { document_id };
    } catch (error) {
      console.error('Error processing team document:', error);
      throw new Error(
        error.response?.data?.error ||
        error.message ||
        'Failed to process team document. Please try again.'
      );
    }
  },


  /**
   * Check document extraction status (polling)
   * @param {string} documentId - Document ID to check
   */
  checkExtractionStatus: async (documentId) => {
    try {
      const response = await api.get(`/manage_docs/document_status/${documentId}`);
      return response.data;
    } catch (error) {
      console.error('Error checking extraction status:', error);
      throw new Error(
        error.response?.data?.error || 
        error.message || 
        'Failed to check document status. Please try again.'
      );
    }
  },

  /**
   * Get all performance entities for a firm
   * Returns entities with their metadata including period counts
   * @param {string} firmId - Firm ID
   */
  getManagerPerformanceEntities: async (firmId) => {
    try {
      const response = await api.get('/performance/entities', {
        params: { firm_id: firmId }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching performance entities:', error);
      throw new Error(
        error.response?.data?.error || 
        error.message || 
        'Failed to fetch performance entities. Please try again.'
      );
    }
  },

  /**
   * [PROVISIONED — not yet active in the manager UI]
   *
   * Invokes the admin data curator on behalf of a manager.  Managers are
   * restricted to their own firm_id; the backend enforces this via
   * caller_role='manager' + caller_firm_id.
   *
   * Replaces the legacy firm_maintenance / maintain_opportunities calls for
   * data upload and extraction.  Actual record creation is still delegated to
   * HFRC_Firms_Management and HFRC_Maintain_Opportunities via the curator commit.
   *
   * @param {object} payload — same shape as adminService.firmUpload
   */
  curateDocument: async (payload) => {
    try {
      const response = await api.post('/manager/utilities/document-upload', payload);
      return response.data;
    } catch (error) {
      console.error('Error in curateDocument:', error);
      throw new Error(
        error.response?.data?.error ||
        error.message ||
        'Document curation request failed.'
      );
    }
  },
};

export default managerService;